import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';

/**
 * Format a numeric string for display in a currency input while the user
 * is mid-typing. Adds thousand separators to the integer portion but
 * preserves any in-progress decimal (e.g. "1500." → "1,500." not "1,500"
 * which would prevent typing decimals at all).
 */
const formatAmountForDisplay = (raw) => {
  if (raw === '' || raw == null) return '';
  const str = String(raw);
  if (!/[0-9]/.test(str)) return str;
  const [intPart, decPart] = str.split('.');
  const intFormatted = intPart === '' ? '' : Number(intPart).toLocaleString('en-US');
  return decPart !== undefined ? `${intFormatted}.${decPart}` : intFormatted;
};
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { FileText, Plus, DollarSign, Calendar, TrendingUp, CreditCard, Receipt, ExternalLink, Users, Percent, Trash2, AlertTriangle, ArrowRightLeft } from 'lucide-react';
import { notesApi, projectsApi, employeesApi } from '../lib/api';
import { formatCurrency, formatCurrencyDetailed, formatDate } from '../lib/utils';
import { toast } from 'sonner';

export const Notes = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [notes, setNotes] = useState([]);
  const [notesSummary, setNotesSummary] = useState(null);
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const [notePayments, setNotePayments] = useState([]);
  const [commissionStatus, setCommissionStatus] = useState(null);
  const [editingNote, setEditingNote] = useState(null);
  const [showSellForm, setShowSellForm] = useState(false);
  
  const [sellFormData, setSellFormData] = useState({
    sale_price: '',
    sale_date: new Date().toISOString().split('T')[0],
    buyer_name: '',
    description: ''
  });
  
  const [noteFormData, setNoteFormData] = useState({
    project_id: '',
    buyer_name: '',
    sale_price: '',        // The total sale price from project
    down_payment: '0',     // Cash received at closing
    principal_amount: '',  // Financed amount = sale_price - down_payment
    interest_rate: '',
    term_months: '',
    monthly_payment: '',
    start_date: new Date().toISOString().split('T')[0],
    balloon_payment: '',
    notes: '',
    // Commission options
    pay_commission_at_closing: true,  // Default: pay lump sum at closing
    employee_id: ''
  });
  
  const [paymentFormData, setPaymentFormData] = useState({
    amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    principal_portion: '',
    interest_portion: '',
    description: '',
    include_commission: false,
    employee_id: ''
  });

  // Calculate principal (financed amount) = Sale Price - Down Payment
  const calculatePrincipal = (salePrice, downPayment) => {
    const sale = parseFloat(salePrice) || 0;
    const down = parseFloat(downPayment) || 0;
    return Math.max(0, sale - down).toFixed(2);
  };

  // Calculate monthly payment using amortization formula
  // M = P * [r(1+r)^n] / [(1+r)^n - 1]
  const calculateMonthlyPayment = (principal, annualRate, termMonths) => {
    const p = parseFloat(principal) || 0;
    const annualRatePercent = parseFloat(annualRate) || 0;
    const n = parseInt(termMonths) || 0;
    
    if (p <= 0 || n <= 0) return '';
    
    // If interest rate is 0, simple division
    if (annualRatePercent <= 0) {
      return (p / n).toFixed(2);
    }
    
    // Monthly interest rate (convert from percentage)
    const r = (annualRatePercent / 100) / 12;
    
    // Amortization formula
    const numerator = r * Math.pow(1 + r, n);
    const denominator = Math.pow(1 + r, n) - 1;
    const monthlyPayment = p * (numerator / denominator);
    
    return monthlyPayment.toFixed(2);
  };

  // Update form field and recalculate dependent fields
  const updateNoteFormField = (field, value) => {
    const newFormData = { ...noteFormData, [field]: value };
    
    // Auto-calculate principal when sale price or down payment changes
    if (['sale_price', 'down_payment'].includes(field)) {
      const principal = calculatePrincipal(
        field === 'sale_price' ? value : newFormData.sale_price,
        field === 'down_payment' ? value : newFormData.down_payment
      );
      newFormData.principal_amount = principal;
      
      // Also recalculate monthly payment with new principal
      if (newFormData.interest_rate && newFormData.term_months) {
        newFormData.monthly_payment = calculateMonthlyPayment(
          principal,
          newFormData.interest_rate,
          newFormData.term_months
        );
      }
    }
    
    // Auto-calculate monthly payment when principal, rate, or term changes
    if (['principal_amount', 'interest_rate', 'term_months'].includes(field)) {
      const calculatedPayment = calculateMonthlyPayment(
        field === 'principal_amount' ? value : newFormData.principal_amount,
        field === 'interest_rate' ? value : newFormData.interest_rate,
        field === 'term_months' ? value : newFormData.term_months
      );
      newFormData.monthly_payment = calculatedPayment;
    }
    
    setNoteFormData(newFormData);
  };

  // Check for URL params to auto-open create form
  useEffect(() => {
    if (searchParams.get('create') === 'true') {
      const projectId = searchParams.get('project_id');
      const salePrice = searchParams.get('principal'); // This is actually sale_price from project
      
      setNoteFormData(prev => ({
        ...prev,
        project_id: projectId || '',
        sale_price: salePrice || '',
        down_payment: '0',
        principal_amount: salePrice || '', // Will be recalculated when down payment entered
      }));
      setShowNoteForm(true);
      
      // Clear URL params
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [notesRes, summaryRes, projectsRes, employeesRes] = await Promise.all([
        notesApi.getAll(),
        notesApi.getSummary(),
        projectsApi.getAll(),
        employeesApi.getAll()
      ]);
      setNotes(notesRes.data);
      setNotesSummary(summaryRes.data);
      setEmployees(employeesRes.data);
      // Filter to projects that can have notes (Owned, Listed, Under_Contract_Sell, or already Note_Held)
      const eligibleProjects = projectsRes.data.filter(p => 
        ['Owned', 'Listed', 'Under_Contract_Sell', 'Note_Held'].includes(p.status)
      );
      setProjects(eligibleProjects);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load notes');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNote = async (e) => {
    e.preventDefault();
    try {
      const noteData = {
        project_id: noteFormData.project_id,
        buyer_name: noteFormData.buyer_name,
        principal_amount: parseFloat(noteFormData.principal_amount),
        interest_rate: parseFloat(noteFormData.interest_rate) / 100, // Convert percentage to decimal
        term_months: parseInt(noteFormData.term_months),
        monthly_payment: parseFloat(noteFormData.monthly_payment),
        down_payment: parseFloat(noteFormData.down_payment) || 0,
        start_date: noteFormData.start_date,
        balloon_payment: noteFormData.balloon_payment ? parseFloat(noteFormData.balloon_payment) : null,
        notes: noteFormData.notes || null,
        // Commission options (only for new notes)
        pay_commission_at_closing: noteFormData.pay_commission_at_closing,
        employee_id: noteFormData.pay_commission_at_closing ? noteFormData.employee_id : null
      };
      
      if (editingNote) {
        // Update existing note (without commission options - those are set at creation)
        const updateData = { ...noteData };
        delete updateData.pay_commission_at_closing;
        delete updateData.employee_id;
        await notesApi.update(editingNote.id, updateData);
        toast.success('Note updated successfully');
      } else {
        // Create new note with commission options
        await notesApi.create(noteData);
        toast.success(noteFormData.pay_commission_at_closing && noteFormData.employee_id 
          ? 'Note created and commission paid at closing' 
          : 'Note created successfully');
      }
      
      setShowNoteForm(false);
      resetNoteForm();
      loadData();
      
      // If we were viewing this note, refresh its data
      if (selectedNote && editingNote && selectedNote.id === editingNote.id) {
        const noteRes = await notesApi.getOne(editingNote.id);
        setSelectedNote(noteRes.data);
      }
    } catch (error) {
      console.error('Failed to save note:', error);
      toast.error(editingNote ? 'Failed to update note' : 'Failed to create note');
    }
  };

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    if (!selectedNote) return;
    
    try {
      // If including commission, use the commission endpoint
      if (paymentFormData.include_commission && paymentFormData.employee_id) {
        await notesApi.recordPaymentWithCommission(selectedNote.id, {
          note_id: selectedNote.id,
          amount: parseFloat(paymentFormData.amount),
          payment_date: paymentFormData.payment_date,
          principal_portion: parseFloat(paymentFormData.principal_portion) || 0,
          interest_portion: parseFloat(paymentFormData.interest_portion) || 0,
          description: paymentFormData.description,
          employee_id: paymentFormData.employee_id
        });
        toast.success('Payment recorded with commission');
      } else {
        await notesApi.recordPayment(selectedNote.id, {
          note_id: selectedNote.id,
          amount: parseFloat(paymentFormData.amount),
          payment_date: paymentFormData.payment_date,
          principal_portion: parseFloat(paymentFormData.principal_portion) || 0,
          interest_portion: parseFloat(paymentFormData.interest_portion) || 0,
          description: paymentFormData.description
        });
        toast.success('Payment recorded');
      }
      setShowPaymentForm(false);
      resetPaymentForm();
      
      // Reload all data
      const [notesRes, summaryRes] = await Promise.all([
        notesApi.getAll(),
        notesApi.getSummary()
      ]);
      setNotes(notesRes.data);
      setNotesSummary(summaryRes.data);
      
      // Update selectedNote with fresh data
      if (selectedNote) {
        const updatedNote = notesRes.data.find(n => n.id === selectedNote.id);
        if (updatedNote) {
          setSelectedNote(updatedNote);
        }
        // Also reload payments
        const paymentsRes = await notesApi.getPayments(selectedNote.id);
        setNotePayments(paymentsRes.data);
        // Also reload commission status
        try {
          const commRes = await notesApi.getCommissionStatus(selectedNote.id);
          setCommissionStatus(commRes.data);
        } catch (e) {
          // Commission tracking may not be set up
        }
      }
    } catch (error) {
      console.error('Failed to record payment:', error);
      toast.error('Failed to record payment');
    }
  };

  const handleNoteSelect = async (note) => {
    setSelectedNote(note);
    try {
      const [paymentsRes, commRes] = await Promise.all([
        notesApi.getPayments(note.id),
        notesApi.getCommissionStatus(note.id).catch(() => ({ data: null }))
      ]);
      setNotePayments(paymentsRes.data);
      setCommissionStatus(commRes.data);
    } catch (error) {
      console.error('Failed to load note data:', error);
    }
  };

  const handleDeleteNote = async (note) => {
    try {
      await notesApi.delete(note.id);
      toast.success('Note deleted successfully');
      setSelectedNote(null);
      setNotePayments([]);
      setCommissionStatus(null);
      loadData();
    } catch (error) {
      console.error('Failed to delete note:', error);
      toast.error('Failed to delete note');
    }
  };

  const handleSellNote = async (e) => {
    e.preventDefault();
    if (!selectedNote) return;
    
    try {
      const result = await notesApi.sellNote(selectedNote.id, {
        sale_price: parseFloat(sellFormData.sale_price),
        sale_date: sellFormData.sale_date,
        buyer_name: sellFormData.buyer_name,
        description: sellFormData.description || null
      });
      
      const details = result.data.sale_details;
      const gainLossText = details.gain_loss >= 0 
        ? `Gain: ${formatCurrency(details.gain_loss)}` 
        : `Discount: ${formatCurrency(Math.abs(details.gain_loss))}`;
      
      toast.success(`Note sold to ${details.buyer_name}. ${gainLossText}`);
      
      setShowSellForm(false);
      setSellFormData({ sale_price: '', sale_date: new Date().toISOString().split('T')[0], buyer_name: '', description: '' });
      
      // Refresh data
      const [notesRes, summaryRes] = await Promise.all([
        notesApi.getAll(),
        notesApi.getSummary()
      ]);
      setNotes(notesRes.data);
      setNotesSummary(summaryRes.data);
      
      // Update selected note with fresh data
      const updatedNote = notesRes.data.find(n => n.id === selectedNote.id);
      if (updatedNote) setSelectedNote(updatedNote);
      
    } catch (error) {
      console.error('Failed to sell note:', error);
      toast.error(error.response?.data?.detail || 'Failed to sell note');
    }
  };

  const resetNoteForm = () => {
    setNoteFormData({
      project_id: '',
      buyer_name: '',
      sale_price: '',
      down_payment: '0',
      principal_amount: '',
      interest_rate: '',
      term_months: '',
      monthly_payment: '',
      start_date: new Date().toISOString().split('T')[0],
      balloon_payment: '',
      notes: '',
      pay_commission_at_closing: true,
      employee_id: ''
    });
    setEditingNote(null);
  };

  // Open form to edit an existing note
  const handleEditNote = (note) => {
    // Find the project to get sale price
    const project = projects.find(p => p.id === note.project_id);
    const salePrice = project?.sale_price || (parseFloat(note.principal_amount) + parseFloat(note.down_payment || 0));
    
    setNoteFormData({
      project_id: note.project_id,
      buyer_name: note.buyer_name || '',
      sale_price: salePrice.toString(),
      down_payment: (note.down_payment || 0).toString(),
      principal_amount: (note.principal_amount || 0).toString(),
      interest_rate: ((note.interest_rate || 0) * 100).toString(), // Convert from decimal to percentage
      term_months: (note.term_months || '').toString(),
      monthly_payment: (note.monthly_payment || '').toString(),
      start_date: note.start_date || new Date().toISOString().split('T')[0],
      balloon_payment: (note.balloon_payment || '').toString(),
      notes: note.notes || ''
    });
    setEditingNote(note);
    setShowNoteForm(true);
  };

  const resetPaymentForm = () => {
    setPaymentFormData({
      amount: '',
      payment_date: new Date().toISOString().split('T')[0],
      principal_portion: '',
      interest_portion: '',
      description: '',
      include_commission: false,
      employee_id: ''
    });
  };

  // Auto-calculate payment portions when amount changes
  const handlePaymentAmountChange = (amount) => {
    if (selectedNote && amount) {
      const amt = parseFloat(amount);
      const rate = selectedNote.interest_rate / 12; // Monthly rate
      const interestPortion = selectedNote.remaining_balance * rate;
      const principalPortion = Math.max(0, amt - interestPortion);
      
      setPaymentFormData({
        ...paymentFormData,
        amount,
        interest_portion: interestPortion.toFixed(2),
        principal_portion: principalPortion.toFixed(2)
      });
    } else {
      setPaymentFormData({ ...paymentFormData, amount });
    }
  };

  const getProjectName = (projectId) => {
    const allProjects = [...projects, ...notes.map(n => ({ id: n.project_id, name: n.buyer_name }))];
    const project = allProjects.find(p => p.id === projectId);
    return project?.name || 'Unknown';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="notes-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-heading font-bold">Notes Receivable</h1>
          <p className="text-xs sm:text-sm text-muted-foreground">Owner-financed deals and payment tracking</p>
        </div>
        <Button 
          onClick={() => setShowNoteForm(true)}
          className="bg-forest-500 hover:bg-forest-600 text-white rounded-sm w-full sm:w-auto"
          data-testid="create-note-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> New Note
        </Button>
      </div>

      {/* Summary Cards */}
      {notesSummary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
          <Card className="rounded-sm border">
            <CardContent className="p-3 sm:pt-4">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-[10px] sm:text-xs text-muted-foreground uppercase">Active Notes</p>
                  <p className="text-lg sm:text-2xl font-mono font-bold">{notesSummary.active_notes_count}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="rounded-sm border">
            <CardContent className="p-3 sm:pt-4">
              <div className="flex items-center gap-2">
                <DollarSign className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-500 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-[10px] sm:text-xs text-muted-foreground uppercase">Total Receivable</p>
                  <p className="text-lg sm:text-2xl font-mono font-bold">{formatCurrency(notesSummary.total_remaining_balance)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="rounded-sm border">
            <CardContent className="p-3 sm:pt-4">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-purple-500 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-[10px] sm:text-xs text-muted-foreground uppercase">Expected Monthly</p>
                  <p className="text-lg sm:text-2xl font-mono font-bold">{formatCurrency(notesSummary.expected_monthly_income)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="rounded-sm border">
            <CardContent className="p-3 sm:pt-4">
              <div className="flex items-center gap-2">
                <Receipt className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-[10px] sm:text-xs text-muted-foreground uppercase">Received This Month</p>
                  <p className="text-lg sm:text-2xl font-mono font-bold">{formatCurrency(notesSummary.payments_received_this_month)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Notes List */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Notes List */}
        <div className="lg:col-span-1">
          <Card className="rounded-sm border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold uppercase tracking-wide">Active Notes</CardTitle>
            </CardHeader>
            <CardContent>
              {notes.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">
                  No owner-financed notes yet. Create one to get started.
                </p>
              ) : (
                <div className="space-y-2">
                  {notes.map(note => {
                    const project = projects.find(p => p.id === note.project_id);
                    return (
                      <div
                        key={note.id}
                        onClick={() => handleNoteSelect(note)}
                        className={`p-3 border rounded-sm cursor-pointer transition-colors ${
                          selectedNote?.id === note.id ? 'bg-forest-50 border-forest-300' : 'hover:bg-muted/50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">{note.buyer_name}</p>
                            <p className="text-xs text-muted-foreground">{project?.name || 'Unknown Project'}</p>
                          </div>
                          <Badge variant="outline" className={`rounded-sm text-xs ${
                            note.status === 'Active' ? 'bg-green-50 text-green-700' : 
                            note.status === 'Paid_Off' ? 'bg-blue-50 text-blue-700' : 
                            note.status === 'Sold' ? 'bg-amber-50 text-amber-700 border-amber-300' :
                            'bg-red-50 text-red-700'
                          }`}>
                            {note.status === 'Sold' ? 'Sold' : note.status}
                          </Badge>
                        </div>
                        <div className="mt-2 flex justify-between text-xs">
                          <span className="text-muted-foreground">Remaining:</span>
                          <span className="font-mono font-medium">{formatCurrency(note.remaining_balance)}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Note Details */}
        <div className="lg:col-span-2">
          {selectedNote ? (
            <Card className="rounded-sm border">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>{selectedNote.buyer_name}</CardTitle>
                    <CardDescription className="space-y-0.5">
                      <Link to={`/projects/${selectedNote.project_id}`} className="text-blue-600 hover:underline flex items-center gap-1">
                        View Project <ExternalLink className="w-3 h-3" />
                      </Link>
                      <p className="text-xs text-muted-foreground">
                        Sale: {formatCurrency((selectedNote.principal_amount || 0) + (selectedNote.down_payment || 0))}
                        {selectedNote.down_payment > 0 && (
                          <span> &middot; Down Payment: {formatCurrency(selectedNote.down_payment)}</span>
                        )}
                      </p>
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="outline"
                          className="rounded-sm text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                          data-testid="delete-note-btn"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="rounded-sm">
                        <AlertDialogHeader>
                          <AlertDialogTitle className="flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5 text-red-500" />
                            Delete Note Receivable?
                          </AlertDialogTitle>
                          <AlertDialogDescription className="space-y-2">
                            <p>
                              Are you sure you want to delete this note for <strong>{selectedNote.buyer_name}</strong>?
                            </p>
                            <div className="bg-red-50 border border-red-200 rounded-sm p-3 text-sm text-red-800">
                              <p className="font-semibold mb-1">Warning: This action cannot be undone.</p>
                              <ul className="list-disc list-inside space-y-1">
                                <li>The note and all payment history will be permanently deleted</li>
                                <li>Commission records linked to this note will remain in the employee ledger</li>
                                <li>The linked project status will NOT be automatically changed</li>
                              </ul>
                            </div>
                            <p className="text-xs text-muted-foreground mt-2">
                              Remaining balance: {formatCurrency(selectedNote.remaining_balance)}
                            </p>
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
                          <AlertDialogAction 
                            onClick={() => handleDeleteNote(selectedNote)}
                            className="bg-red-600 hover:bg-red-700 rounded-sm"
                          >
                            Delete Note
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                    <Button
                      onClick={() => handleEditNote(selectedNote)}
                      variant="outline"
                      className="rounded-sm"
                      data-testid="edit-note-btn"
                      disabled={selectedNote.status === 'Sold'}
                    >
                      Edit Note
                    </Button>
                    {selectedNote.status === 'Active' && (
                      <Button
                        onClick={() => {
                          setSellFormData({
                            sale_price: '',
                            sale_date: new Date().toISOString().split('T')[0],
                            buyer_name: '',
                            description: ''
                          });
                          setShowSellForm(true);
                        }}
                        variant="outline"
                        className="rounded-sm text-amber-700 hover:text-amber-800 hover:bg-amber-50 border-amber-300"
                        data-testid="sell-note-btn"
                      >
                        <ArrowRightLeft className="w-4 h-4 mr-2" /> Sell Note
                      </Button>
                    )}
                    <Button
                      onClick={() => setShowPaymentForm(true)}
                      className="bg-forest-500 hover:bg-forest-600 text-white rounded-sm"
                      disabled={selectedNote.status !== 'Active'}
                    >
                      <CreditCard className="w-4 h-4 mr-2" /> Record Payment
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="details">
                  <TabsList className="bg-muted/50 rounded-sm">
                    <TabsTrigger value="details" className="rounded-sm">Details</TabsTrigger>
                    <TabsTrigger value="payments" className="rounded-sm">Payments ({notePayments.length})</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="details" className="mt-4">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <div className="p-3 bg-muted/30 rounded-sm">
                        <p className="text-xs text-muted-foreground">Principal Amount</p>
                        <p className="text-lg font-mono font-semibold">{formatCurrency(selectedNote.principal_amount)}</p>
                      </div>
                      <div className="p-3 bg-muted/30 rounded-sm">
                        <p className="text-xs text-muted-foreground">Interest Rate</p>
                        <p className="text-lg font-mono font-semibold">{(selectedNote.interest_rate * 100).toFixed(2)}%</p>
                      </div>
                      <div className="p-3 bg-muted/30 rounded-sm">
                        <p className="text-xs text-muted-foreground">Term</p>
                        <p className="text-lg font-mono font-semibold">{selectedNote.term_months} months</p>
                      </div>
                      <div className="p-3 bg-muted/30 rounded-sm">
                        <p className="text-xs text-muted-foreground">Monthly Payment</p>
                        <p className="text-lg font-mono font-semibold">{formatCurrency(selectedNote.monthly_payment)}</p>
                      </div>
                      <div className="p-3 bg-muted/30 rounded-sm">
                        <p className="text-xs text-muted-foreground">Start Date</p>
                        <p className="text-lg font-mono font-semibold">{formatDate(selectedNote.start_date)}</p>
                      </div>
                      {selectedNote.balloon_payment && (
                        <div className="p-3 bg-purple-50 rounded-sm">
                          <p className="text-xs text-muted-foreground">Balloon Payment</p>
                          <p className="text-lg font-mono font-semibold text-purple-600">{formatCurrency(selectedNote.balloon_payment)}</p>
                        </div>
                      )}
                      <div className="p-3 bg-emerald-50 rounded-sm">
                        <p className="text-xs text-muted-foreground">Payments Received</p>
                        <p className="text-lg font-mono font-semibold text-emerald-600">{formatCurrency(selectedNote.total_received - (selectedNote.down_payment || 0))}</p>
                      </div>
                      <div className="p-3 bg-amber-50 rounded-sm">
                        <p className="text-xs text-muted-foreground">Remaining Balance</p>
                        <p className="text-lg font-mono font-semibold text-amber-600">{formatCurrency(selectedNote.remaining_balance)}</p>
                      </div>
                    </div>
                    
                    {/* Progress bar - monthly payments toward principal */}
                    <div className="mt-4">
                      <div className="flex justify-between text-xs mb-1">
                        <span>Payment Progress</span>
                        <span>{(((selectedNote.total_received - (selectedNote.down_payment || 0)) / selectedNote.principal_amount) * 100).toFixed(1)}%</span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-2">
                        <div 
                          className="bg-forest-500 h-2 rounded-full transition-all"
                          style={{ width: `${Math.min(100, ((selectedNote.total_received - (selectedNote.down_payment || 0)) / selectedNote.principal_amount) * 100)}%` }}
                        />
                      </div>
                    </div>
                    
                    {/* Note Sale Info - for sold notes */}
                    {selectedNote.status === 'Sold' && (
                      <div className="mt-4 p-4 bg-amber-50 rounded-sm border border-amber-200" data-testid="note-sale-info">
                        <div className="flex items-center gap-2 mb-3">
                          <ArrowRightLeft className="w-4 h-4 text-amber-700" />
                          <p className="text-sm font-semibold text-amber-800">Note Sold on Secondary Market</p>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                          <div>
                            <p className="text-xs text-muted-foreground">Sold To</p>
                            <p className="font-medium">{selectedNote.note_buyer_name}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Sale Price</p>
                            <p className="font-mono font-medium">{formatCurrency(selectedNote.note_sale_price)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Sale Date</p>
                            <p className="font-medium">{formatDate(selectedNote.note_sale_date)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">
                              {selectedNote.note_sale_gain_loss >= 0 ? 'Gain' : 'Discount Taken'}
                            </p>
                            <p className={`font-mono font-medium ${selectedNote.note_sale_gain_loss >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                              {selectedNote.note_sale_gain_loss >= 0 ? '+' : '-'}{formatCurrency(Math.abs(selectedNote.note_sale_gain_loss || 0))}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {selectedNote.notes && (
                      <div className="mt-4 p-3 bg-muted/30 rounded-sm">
                        <p className="text-xs text-muted-foreground mb-1">Notes</p>
                        <p className="text-sm">{selectedNote.notes}</p>
                      </div>
                    )}
                  </TabsContent>
                  
                  <TabsContent value="payments" className="mt-4">
                    {notePayments.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-8">
                        No payments recorded yet.
                      </p>
                    ) : (
                      <div className="border rounded-sm overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
                        <table className="data-table min-w-[450px]">
                          <thead>
                            <tr>
                              <th>Date</th>
                              <th>Amount</th>
                              <th>Principal</th>
                              <th>Interest</th>
                              <th>Description</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {notePayments.map((payment) => (
                              <tr key={payment.id}>
                                <td>{formatDate(payment.payment_date)}</td>
                                <td className="font-mono">{formatCurrencyDetailed(payment.amount)}</td>
                                <td className="font-mono text-emerald-600">{formatCurrencyDetailed(payment.principal_portion)}</td>
                                <td className="font-mono text-blue-600">{formatCurrencyDetailed(payment.interest_portion)}</td>
                                <td className="text-muted-foreground">{payment.description || '-'}</td>
                                <td>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                    title="Delete payment"
                                    data-testid={`delete-payment-${payment.id}`}
                                    onClick={async () => {
                                      if (!window.confirm('Delete this payment? The note balance will be recalculated.')) return;
                                      try {
                                        await notesApi.deletePayment(selectedNote.id, payment.id);
                                        toast.success('Payment deleted');
                                        // Reload payments for this note
                                        const paymentsRes = await notesApi.getPayments(selectedNote.id);
                                        setNotePayments(paymentsRes.data);
                                        // Reload all notes data to refresh balances
                                        const [notesRes, summaryRes] = await Promise.all([
                                          notesApi.getAll(),
                                          notesApi.getSummary()
                                        ]);
                                        setNotes(notesRes.data);
                                        setNotesSummary(summaryRes.data);
                                        // Update selected note with fresh data
                                        const updatedNote = notesRes.data.find(n => n.id === selectedNote.id);
                                        if (updatedNote) setSelectedNote(updatedNote);
                                      } catch (error) {
                                        toast.error(error?.response?.data?.detail || 'Failed to delete payment');
                                      }
                                    }}
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          ) : (
            <Card className="rounded-sm border">
              <CardContent className="py-16 text-center">
                <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">Select a note to view details</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Create/Edit Note Dialog */}
      <Dialog open={showNoteForm} onOpenChange={(open) => { setShowNoteForm(open); if (!open) resetNoteForm(); }}>
        <DialogContent className="rounded-sm max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingNote ? 'Edit Note' : 'Create Owner-Financed Note'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateNote} className="space-y-4">
            <div className="space-y-2">
              <Label>Property/Project</Label>
              <Select 
                value={noteFormData.project_id} 
                onValueChange={(v) => {
                  // When project changes, auto-fill sale price
                  const project = projects.find(p => p.id === v);
                  if (project?.sale_price) {
                    updateNoteFormField('sale_price', project.sale_price.toString());
                    setNoteFormData(prev => ({ ...prev, project_id: v, sale_price: project.sale_price.toString() }));
                  } else {
                    setNoteFormData(prev => ({ ...prev, project_id: v }));
                  }
                }}
                disabled={!!editingNote}
              >
                <SelectTrigger className="rounded-sm">
                  <SelectValue placeholder="Select a property" />
                </SelectTrigger>
                <SelectContent>
                  {projects.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name} {p.sale_price ? `- ${formatCurrency(p.sale_price)}` : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Buyer Name</Label>
              <Input 
                value={noteFormData.buyer_name}
                onChange={(e) => setNoteFormData({...noteFormData, buyer_name: e.target.value})}
                placeholder="John Smith"
                className="rounded-sm"
                required
              />
            </div>
            
            {/* Sale Price, Down Payment, and Principal (Financed Amount) */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Sale Price ($)</Label>
                <Input 
                  type="text"
                  value={formatAmountForDisplay(noteFormData.sale_price)}
                  onChange={(e) => updateNoteFormField('sale_price', e.target.value.replace(/[^0-9.]/g, ''))}
                  placeholder="45,000"
                  className="rounded-sm"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Down Payment ($)</Label>
                <Input 
                  type="text"
                  value={formatAmountForDisplay(noteFormData.down_payment)}
                  onChange={(e) => updateNoteFormField('down_payment', e.target.value.replace(/[^0-9.]/g, ''))}
                  placeholder="5,000"
                  className="rounded-sm"
                />
              </div>
              <div className="space-y-2">
                <Label>Financed Amount ($)</Label>
                <Input 
                  type="text"
                  value={formatAmountForDisplay(noteFormData.principal_amount)}
                  onChange={(e) => updateNoteFormField('principal_amount', e.target.value.replace(/[^0-9.]/g, ''))}
                  placeholder="Auto-calculated"
                  className="rounded-sm bg-muted/50"
                  required
                />
                <p className="text-xs text-muted-foreground">= Sale - Down Payment</p>
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Interest Rate (%)</Label>
                <Input 
                  type="number"
                  step="0.01"
                  value={noteFormData.interest_rate}
                  onChange={(e) => updateNoteFormField('interest_rate', e.target.value)}
                  placeholder="8.5"
                  className="rounded-sm"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Term (Months)</Label>
                <Input 
                  type="number"
                  value={noteFormData.term_months}
                  onChange={(e) => updateNoteFormField('term_months', e.target.value)}
                  placeholder="60"
                  className="rounded-sm"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Monthly Payment ($)</Label>
                <Input 
                  type="text"
                  value={formatAmountForDisplay(noteFormData.monthly_payment)}
                  onChange={(e) => setNoteFormData({...noteFormData, monthly_payment: e.target.value.replace(/[^0-9.]/g, '')})}
                  placeholder="Auto-calculated"
                  className="rounded-sm"
                  required
                />
                <p className="text-xs text-muted-foreground">Auto-calculated, editable</p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input 
                  type="date"
                  value={noteFormData.start_date}
                  onChange={(e) => setNoteFormData({...noteFormData, start_date: e.target.value})}
                  className="rounded-sm"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Balloon Payment ($)</Label>
                <Input 
                  type="text"
                  value={formatAmountForDisplay(noteFormData.balloon_payment)}
                  onChange={(e) => setNoteFormData({...noteFormData, balloon_payment: e.target.value.replace(/[^0-9.]/g, '')})}
                  placeholder="Optional"
                  className="rounded-sm"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Notes</Label>
              <Input 
                value={noteFormData.notes}
                onChange={(e) => setNoteFormData({...noteFormData, notes: e.target.value})}
                placeholder="Any additional notes..."
                className="rounded-sm"
              />
            </div>
            
            {/* Commission Section - Only show for new notes */}
            {!editingNote && (
              <div className="border-t pt-4 mt-4">
                <div className="flex items-center gap-2 mb-3">
                  <Checkbox 
                    id="pay_commission_at_closing"
                    checked={noteFormData.pay_commission_at_closing}
                    onCheckedChange={(checked) => setNoteFormData({...noteFormData, pay_commission_at_closing: checked})}
                  />
                  <Label htmlFor="pay_commission_at_closing" className="text-sm font-medium cursor-pointer">
                    Pay commission at closing (lump sum)
                  </Label>
                </div>
                
                {noteFormData.pay_commission_at_closing && (
                  <div className="space-y-3 pl-6">
                    <div className="space-y-2">
                      <Label>Employee to Pay Commission To</Label>
                      <Select 
                        value={noteFormData.employee_id} 
                        onValueChange={(v) => setNoteFormData({...noteFormData, employee_id: v})}
                      >
                        <SelectTrigger className="rounded-sm">
                          <SelectValue placeholder="Select an employee" />
                        </SelectTrigger>
                        <SelectContent>
                          {employees.map(emp => (
                            <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <p className="text-xs text-muted-foreground bg-green-50 p-2 rounded-sm">
                      <strong>Lump Sum:</strong> Full commission will be calculated and paid immediately at closing based on land finder profit.
                    </p>
                  </div>
                )}
                
                {!noteFormData.pay_commission_at_closing && (
                  <p className="text-xs text-muted-foreground bg-amber-50 p-2 rounded-sm pl-6">
                    <strong>Deferred:</strong> Commission can be paid later when recording note payments (proportional payout option).
                  </p>
                )}
              </div>
            )}
            
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => { setShowNoteForm(false); resetNoteForm(); }} className="rounded-sm">
                Cancel
              </Button>
              <Button type="submit" className="bg-forest-500 hover:bg-forest-600 text-white rounded-sm">
                {editingNote ? 'Save Changes' : 'Create Note'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Record Payment Dialog */}
      <Dialog open={showPaymentForm} onOpenChange={setShowPaymentForm}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle>Record Payment - {selectedNote?.buyer_name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleRecordPayment} className="space-y-4">
            <div className="p-3 bg-muted/30 rounded-sm text-sm">
              <div className="flex justify-between">
                <span>Expected Payment:</span>
                <span className="font-mono">{formatCurrency(selectedNote?.monthly_payment)}</span>
              </div>
              <div className="flex justify-between">
                <span>Remaining Balance:</span>
                <span className="font-mono">{formatCurrency(selectedNote?.remaining_balance)}</span>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Payment Date</Label>
                <Input 
                  type="date"
                  value={paymentFormData.payment_date}
                  onChange={(e) => setPaymentFormData({...paymentFormData, payment_date: e.target.value})}
                  className="rounded-sm"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Total Amount ($)</Label>
                <Input 
                  type="text"
                  inputMode="decimal"
                  value={formatAmountForDisplay(paymentFormData.amount)}
                  onChange={(e) => handlePaymentAmountChange(e.target.value.replace(/[^0-9.]/g, ''))}
                  placeholder={selectedNote?.monthly_payment ? Number(selectedNote.monthly_payment).toLocaleString('en-US') : ''}
                  className="rounded-sm"
                  required
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Principal Portion ($)</Label>
                <Input 
                  type="text"
                  inputMode="decimal"
                  value={formatAmountForDisplay(paymentFormData.principal_portion)}
                  onChange={(e) => setPaymentFormData({...paymentFormData, principal_portion: e.target.value.replace(/[^0-9.]/g, '')})}
                  className="rounded-sm"
                />
                <p className="text-xs text-muted-foreground">Auto-calculated</p>
              </div>
              <div className="space-y-2">
                <Label>Interest Portion ($)</Label>
                <Input 
                  type="text"
                  inputMode="decimal"
                  value={formatAmountForDisplay(paymentFormData.interest_portion)}
                  onChange={(e) => setPaymentFormData({...paymentFormData, interest_portion: e.target.value.replace(/[^0-9.]/g, '')})}
                  className="rounded-sm"
                />
                <p className="text-xs text-muted-foreground">Auto-calculated</p>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Description</Label>
              <Input 
                value={paymentFormData.description}
                onChange={(e) => setPaymentFormData({...paymentFormData, description: e.target.value})}
                placeholder="Optional note about this payment"
                className="rounded-sm"
              />
            </div>
            
            {/* Commission Tracking Section */}
            <div className="border-t pt-4 mt-4">
              <div className="flex items-center gap-2 mb-3">
                <Checkbox 
                  id="include_commission"
                  checked={paymentFormData.include_commission}
                  onCheckedChange={(checked) => setPaymentFormData({...paymentFormData, include_commission: checked})}
                />
                <Label htmlFor="include_commission" className="text-sm font-medium cursor-pointer">
                  Pay proportional commission with this payment
                </Label>
              </div>
              
              {paymentFormData.include_commission && (
                <div className="space-y-3 pl-6">
                  <div className="space-y-2">
                    <Label>Employee to Pay Commission To</Label>
                    <Select 
                      value={paymentFormData.employee_id} 
                      onValueChange={(v) => setPaymentFormData({...paymentFormData, employee_id: v})}
                    >
                      <SelectTrigger className="rounded-sm">
                        <SelectValue placeholder="Select an employee" />
                      </SelectTrigger>
                      <SelectContent>
                        {employees.map(emp => (
                          <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="text-xs text-muted-foreground bg-blue-50 p-2 rounded-sm">
                    <strong>Note:</strong> Commission is calculated on the land finder's profit share at time of sale, 
                    then paid out proportionally as principal payments are received.
                  </p>
                </div>
              )}
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowPaymentForm(false)} className="rounded-sm">
                Cancel
              </Button>
              <Button type="submit" className="bg-forest-500 hover:bg-forest-600 text-white rounded-sm">
                Record Payment
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Sell Note Dialog */}
      <Dialog open={showSellForm} onOpenChange={setShowSellForm}>
        <DialogContent className="rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-amber-600" />
              Sell Note - Secondary Market
            </DialogTitle>
          </DialogHeader>
          {selectedNote && (
            <form onSubmit={handleSellNote} className="space-y-4">
              <div className="p-3 bg-muted/30 rounded-sm text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Original Buyer:</span>
                  <span className="font-medium">{selectedNote.buyer_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Remaining Balance:</span>
                  <span className="font-mono font-medium">{formatCurrency(selectedNote.remaining_balance)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Monthly Payment:</span>
                  <span className="font-mono">{formatCurrency(selectedNote.monthly_payment)}</span>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Note Buyer (Who are you selling to?)</Label>
                <Input 
                  value={sellFormData.buyer_name}
                  onChange={(e) => setSellFormData({...sellFormData, buyer_name: e.target.value})}
                  placeholder="e.g. ABC Note Fund"
                  className="rounded-sm"
                  required
                  data-testid="sell-note-buyer-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Sale Price ($)</Label>
                  <Input 
                    type="text"
                    value={formatAmountForDisplay(sellFormData.sale_price)}
                    onChange={(e) => setSellFormData({...sellFormData, sale_price: e.target.value.replace(/[^0-9.]/g, '')})}
                    placeholder="0"
                    className="rounded-sm"
                    required
                    data-testid="sell-note-price-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Sale Date</Label>
                  <Input 
                    type="date"
                    value={sellFormData.sale_date}
                    onChange={(e) => setSellFormData({...sellFormData, sale_date: e.target.value})}
                    className="rounded-sm"
                    required
                    data-testid="sell-note-date-input"
                  />
                </div>
              </div>
              
              {/* Show gain/loss preview */}
              {sellFormData.sale_price && (
                <div className={`p-3 rounded-sm border text-sm ${
                  parseFloat(sellFormData.sale_price) >= selectedNote.remaining_balance 
                    ? 'bg-emerald-50 border-emerald-200' 
                    : 'bg-red-50 border-red-200'
                }`}>
                  <div className="flex justify-between">
                    <span>Sale Price:</span>
                    <span className="font-mono">{formatCurrency(parseFloat(sellFormData.sale_price) || 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Remaining Balance:</span>
                    <span className="font-mono">-{formatCurrency(selectedNote.remaining_balance)}</span>
                  </div>
                  <div className="flex justify-between font-semibold border-t mt-1 pt-1">
                    <span>{parseFloat(sellFormData.sale_price) >= selectedNote.remaining_balance ? 'Gain:' : 'Discount:'}</span>
                    <span className={`font-mono ${
                      parseFloat(sellFormData.sale_price) >= selectedNote.remaining_balance ? 'text-emerald-700' : 'text-red-700'
                    }`}>
                      {formatCurrency(Math.abs((parseFloat(sellFormData.sale_price) || 0) - selectedNote.remaining_balance))}
                    </span>
                  </div>
                </div>
              )}
              
              <div className="space-y-2">
                <Label>Notes (optional)</Label>
                <Input 
                  value={sellFormData.description}
                  onChange={(e) => setSellFormData({...sellFormData, description: e.target.value})}
                  placeholder="Any details about the sale..."
                  className="rounded-sm"
                  data-testid="sell-note-description-input"
                />
              </div>
              
              <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-sm text-amber-800">
                <p className="font-semibold mb-1">What happens when you sell:</p>
                <ul className="list-disc list-inside space-y-0.5 text-xs">
                  <li>The note status will change to &quot;Sold&quot;</li>
                  <li>A Note Sale transaction will be recorded on the project</li>
                  <li>You will no longer receive monthly payments on this note</li>
                  <li>Any remaining commission obligations will be settled proportionally</li>
                </ul>
              </div>
              
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowSellForm(false)} className="rounded-sm">
                  Cancel
                </Button>
                <Button type="submit" className="bg-amber-600 hover:bg-amber-700 text-white rounded-sm" data-testid="confirm-sell-note-btn">
                  <ArrowRightLeft className="w-4 h-4 mr-2" /> Confirm Sale
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
